import apiClient from './apiClient';
import { firestoreService } from './firestoreService';
import { auth, db, storage } from './firebaseConfig';
import { collection, addDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export interface ImageAnalysis {
  labels?: Array<{
    description: string;
    score: number;
    confidence: number;
  }>;
  colors: string[];
  style: string;
  mood: string;
  objects: Array<string | {
    name: string;
    score: number;
    position: string;
  }>;
  webEntities?: Array<{
    description: string;
    score: number;
  }>;
  logos?: Array<{
    description: string;
    score: number;
  }>;
  text: string[];
  cutoutDetected: boolean;
}

export interface SuggestedStyle {
  key: string;
  displayName: string;
  description: string;
}

export interface ImageAnalysisResponse {
  analysisId: string;
  imageUrl: string;
  filename: string;
  ready: boolean;
}

export interface WallpaperGenerationRequest {
  filename: string;
  customPrompt?: string;
}

export interface Wallpaper {
  id: string;
  url: string;
  thumbnail: string;
  style: string;
  isWatermarked: boolean;
  metadata?: {
    width: number;
    height: number;
    method: string;
    controlStrength: number;
  };
}

export interface GenerationStatusResponse {
  generationId: string;
  status: 'processing' | 'completed' | 'failed';
  description?: string;
  wallpapers?: Wallpaper[];
  prompt?: string;
  error?: string;
  debug?: {
    cannyEdge: string;
    originalCropped: string;
  };
}

export interface UserStats {
  totalGenerated: number;
  totalSaved: number;
  totalShared: number;
  todayGenerated: number;
}

export interface QuotaStatus {
  canGenerate: boolean;
  remaining: number;
  used: number;
  limit: number;
}

class WallpaperService {
  /**
   * 上传裁切后的图片并分析
   * @param croppedImageBlob 裁切后的图片 Blob
   * @returns 分析结果
   */
  async analyzeCroppedImage(croppedImageBlob: Blob): Promise<ImageAnalysisResponse> {
    try {
      const formData = new FormData();
      formData.append('image', croppedImageBlob, 'cropped.png');
      
      const response = await apiClient.post('/images/analyze', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data.data;
    } catch (error) {
      console.error('图片分析失败:', error);
      throw error;
    }
  }

  /**
   * 生成壁纸（使用 Stability AI ControlNet）
   * @param request 生成请求
   * @returns 生成结果
   */
  async generateWallpaper(request: WallpaperGenerationRequest): Promise<GenerationStatusResponse> {
    try {
      const response = await apiClient.post('/images/generate', request);
      const result = response.data.data;
      
      // 自动保存生成的壁纸到 Firestore
      try {
        await this.saveGeneratedWallpaperToFirestore(result);
      } catch (firestoreError) {
        console.warn('保存壁纸到 Firestore 失败，但生成成功:', firestoreError);
      }
      
      return result;
    } catch (error) {
      console.error('壁纸生成失败:', error);
      throw error;
    }
  }

  /**
   * 直接从裁切图片生成壁纸（推荐使用）
   * @param croppedImageBlob 裁切后的图片
   * @param customPrompt 自定义提示词
   * @param width 目标宽度
   * @param height 目标高度
   * @returns 生成结果
   */
  async generateFromCropped(croppedImageBlob: Blob, customPrompt?: string, width?: number, height?: number): Promise<GenerationStatusResponse> {
    try {
      const formData = new FormData();
      formData.append('croppedImage', croppedImageBlob, 'cropped.png');
      if (customPrompt) {
        formData.append('customPrompt', customPrompt);
      }
      if (width) {
        formData.append('width', width.toString());
      }
      if (height) {
        formData.append('height', height.toString());
      }
      
      const response = await apiClient.post('/images/generate-from-cropped', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const result = response.data.data;
      
      // 自动保存生成的壁纸到 Firestore
      console.log('🔥 准备保存壁纸到 Firestore...');
      console.log('🔥 生成结果类型:', typeof result);
      console.log('🔥 生成结果键:', Object.keys(result));
      
      try {
        console.log('🔥 开始调用 saveGeneratedWallpaperToFirestore...');
        await this.saveGeneratedWallpaperToFirestore(result);
        console.log('✅ 壁纸保存到 Firestore 成功');
      } catch (firestoreError) {
        console.error('❌ 保存壁纸到 Firestore 失败，但生成成功:', firestoreError);
        if (firestoreError instanceof Error) {
          console.error('❌ Firestore 错误详情:', firestoreError.message);
          console.error('❌ Firestore 错误堆栈:', firestoreError.stack);
        }
      }
      
      return result;
    } catch (error) {
      console.error('壁纸生成失败:', error);
      throw error;
    }
  }

  // 检查生成状态
  async checkGenerationStatus(generationId: string): Promise<GenerationStatusResponse> {
    try {
      const response = await apiClient.get(`/images/generate/status/${generationId}`);
      return response.data;
    } catch (error) {
      console.error('检查生成状态失败:', error);
      throw error;
    }
  }

  // 获取用户历史记录
  async getHistory(page: number = 1, limit: number = 20): Promise<Wallpaper[]> {
    try {
      const response = await apiClient.get('/wallpapers/history', {
        params: { page, limit }
      });
      
      return response.data.wallpapers;
    } catch (error) {
      console.error('获取历史记录失败:', error);
      throw error;
    }
  }

  // 保存壁纸
  async saveWallpaper(wallpaperId: string): Promise<void> {
    try {
      await apiClient.post('/wallpapers/save', { wallpaperId });
    } catch (error) {
      console.error('保存壁纸失败:', error);
      throw error;
    }
  }

  // 删除壁纸
  async deleteWallpaper(wallpaperId: string): Promise<void> {
    try {
      await apiClient.delete(`/wallpapers/${wallpaperId}`);
    } catch (error) {
      console.error('删除壁纸失败:', error);
      throw error;
    }
  }

  // 分享壁纸
  async shareWallpaper(wallpaperId: string): Promise<string> {
    try {
      const response = await apiClient.post('/wallpapers/share', { wallpaperId });
      return response.data.shareUrl;
    } catch (error) {
      console.error('分享壁纸失败:', error);
      throw error;
    }
  }

  // 下载壁纸
  async downloadWallpaper(wallpaperUrl: string, filename: string = 'wallpaper.png'): Promise<void> {
    try {
      // If it's a base64 data URL, download directly
      if (wallpaperUrl.startsWith('data:image')) {
        const link = document.createElement('a');
        link.href = wallpaperUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      // Otherwise, fetch the image
      const response = await fetch(wallpaperUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('下载壁纸失败:', error);
      throw error;
    }
  }

  // 获取用户统计信息
  async getUserStats(): Promise<UserStats> {
    try {
      const response = await apiClient.get('/wallpapers/stats');
      return response.data;
    } catch (error) {
      console.error('获取统计信息失败:', error);
      // 返回默认值
      return {
        totalGenerated: 0,
        totalSaved: 0,
        totalShared: 0,
        todayGenerated: 0
      };
    }
  }

  // 获取推荐壁纸
  async getRecommendedWallpapers(): Promise<Wallpaper[]> {
    try {
      const response = await apiClient.get('/wallpapers/recommended');
      return response.data;
    } catch (error) {
      console.error('获取推荐壁纸失败:', error);
      return [];
    }
  }

  // 搜索壁纸
  async searchWallpapers(query: string, page: number = 1): Promise<Wallpaper[]> {
    try {
      const response = await apiClient.get('/wallpapers/search', {
        params: { query, page }
      });
      
      return response.data.wallpapers;
    } catch (error) {
      console.error('搜索壁纸失败:', error);
      return [];
    }
  }

  // 获取壁纸详情
  async getWallpaperDetails(wallpaperId: string): Promise<Wallpaper> {
    try {
      const response = await apiClient.get(`/wallpapers/${wallpaperId}`);
      return response.data;
    } catch (error) {
      console.error('获取壁纸详情失败:', error);
      throw error;
    }
  }

  // 批量操作
  async batchOperation(operation: 'save' | 'delete' | 'share', wallpaperIds: string[]): Promise<void> {
    try {
      await apiClient.post('/wallpapers/batch', {
        operation,
        wallpaperIds
      });
    } catch (error) {
      console.error('批量操作失败:', error);
      throw error;
    }
  }

  // 获取用户每日配额状态
  async getQuotaStatus(): Promise<QuotaStatus> {
    try {
      const response = await apiClient.get('/images/quota-status');
      return response.data.data;
    } catch (error) {
      console.error('获取配额状态失败:', error);
      // 返回默认值，允许生成
      return {
        canGenerate: true,
        remaining: 3,
        used: 0,
        limit: 3
      };
    }
  }

  // 压缩 base64 图片数据
  private async compressImageDataUrl(dataUrl: string, maxSizeKB: number = 500): Promise<string> {
    try {
      console.log('🔥 开始压缩图片数据...');
      
      // 创建图片元素
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      return new Promise((resolve, reject) => {
        img.onload = () => {
          // 计算压缩比例
          let { width, height } = img;
          const maxDimension = 800; // 最大尺寸
          
          if (width > height) {
            if (width > maxDimension) {
              height = (height * maxDimension) / width;
              width = maxDimension;
            }
          } else {
            if (height > maxDimension) {
              width = (width * maxDimension) / height;
              height = maxDimension;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // 绘制压缩后的图片
          ctx?.drawImage(img, 0, 0, width, height);
          
          // 转换为 base64，使用较低质量
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
          
          console.log('✅ 图片压缩完成，原始大小:', dataUrl.length, '压缩后大小:', compressedDataUrl.length);
          resolve(compressedDataUrl);
        };
        
        img.onerror = () => {
          console.warn('❌ 图片压缩失败，使用原始数据');
          resolve(dataUrl);
        };
        
        img.src = dataUrl;
      });
    } catch (error) {
      console.error('❌ 图片压缩失败:', error);
      return dataUrl; // 返回原始数据
    }
  }

  // 保存生成的壁纸到 Firestore
  private async saveGeneratedWallpaperToFirestore(result: GenerationStatusResponse): Promise<void> {
    try {
      console.log('🔥 开始保存壁纸到 Firestore...');
      console.log('🔥 当前用户:', auth.currentUser);
      console.log('🔥 生成结果:', result);
      
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.warn('❌ 用户未登录，无法保存壁纸到 Firestore');
        return;
      }

      // 测试 Firestore 连接
      console.log('🔥 测试 Firestore 连接...');
      try {
        const testRef = collection(db, 'test');
        const testDoc = await addDoc(testRef, { test: true, timestamp: new Date() });
        console.log('✅ Firestore 连接测试成功:', testDoc.id);
        // 删除测试文档
        await deleteDoc(testDoc);
        console.log('✅ 测试文档已删除');
      } catch (testError) {
        console.error('❌ Firestore 连接测试失败:', testError);
        throw testError;
      }

      if (result.wallpapers && result.wallpapers.length > 0) {
        const wallpaper = result.wallpapers[0]; // 保存第一张壁纸
        
        console.log('🔥 开始处理壁纸数据...');
        
        // 压缩图片数据以避免 Firestore 字段大小限制
        let compressedThumbnail = wallpaper.thumbnail || '';
        let compressedFullImage = wallpaper.url || '';
        
        if (wallpaper.thumbnail) {
          compressedThumbnail = await this.compressImageDataUrl(wallpaper.thumbnail, 200); // 缩略图限制 200KB
        }
        
        if (wallpaper.url) {
          compressedFullImage = await this.compressImageDataUrl(wallpaper.url, 500); // 完整图片限制 500KB
        }
        
        const wallpaperData = {
          userId: currentUser.uid,
          title: 'Generated Wallpaper',
          style: 'generated' as const,
          thumbnail: compressedThumbnail,
          fullImageUrl: compressedFullImage,
          isWatermarked: wallpaper.isWatermarked || false,
          downloads: 0,
          // 移除prompt字段，不保存到历史记录中
          dimensions: {
            width: 1170,
            height: 2532
          }
        };
        
        console.log('🔥 准备保存的壁纸数据:', wallpaperData);
        console.log('🔥 壁纸数据类型检查:', {
          userId: typeof wallpaperData.userId,
          title: typeof wallpaperData.title,
          style: typeof wallpaperData.style,
          thumbnail: typeof wallpaperData.thumbnail,
          fullImageUrl: typeof wallpaperData.fullImageUrl,
          isWatermarked: typeof wallpaperData.isWatermarked,
          downloads: typeof wallpaperData.downloads,
          dimensions: typeof wallpaperData.dimensions
        });
        
        const wallpaperId = await firestoreService.saveWallpaper(wallpaperData);

        console.log('✅ 生成的壁纸已保存到 Firestore，ID:', wallpaperId);
      } else {
        console.warn('❌ 没有壁纸数据可保存');
      }
    } catch (error) {
      console.error('❌ 保存壁纸到 Firestore 失败:', error);
      if (error instanceof Error) {
        console.error('❌ 错误详情:', error.message);
        console.error('❌ 错误堆栈:', error.stack);
      }
      throw error;
    }
  }
}

export const wallpaperService = new WallpaperService();
export default wallpaperService;
